import React from "react";
import { MapContainer, TileLayer, Marker, Polygon, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MAP_TILE_CONFIG } from "@racoon/lib";
import type { LatLngExpression } from "leaflet";
import { cn } from "@racoon/ui";
import { SITE_PIN_ICON } from "~/shared/ui/geofence-editor/site-pin-icon";

interface RecenterProps {
  center: LatLngExpression;
}

const RecenterMap: React.FC<RecenterProps> = ({ center }) => {
  const map = useMap();

  React.useEffect(() => {
    map.setView(center);
  }, [center, map]);

  return null;
};

interface ContainerLocationMapProps {
  center: LatLngExpression;
  selectedLocation?: { lat: number; lon: number; name?: string } | null;
  /** The selected location's geofence, one entry per polygon (ring 0 outline, then holes). */
  polygons?: [number, number][][][];
  isLoading: boolean;
  statusMessage?: string;
  /** Controls drawn over the map's top-left corner (the site search in the container dialog). */
  overlay?: React.ReactNode;
  className?: string;
}

// Border/action for the geofence outline, with a light fill of the same hue.
const GEOFENCE_COLOR = "#3b82f6";

export const ContainerLocationMap: React.FC<ContainerLocationMapProps> = ({
  center,
  selectedLocation,
  polygons = [],
  isLoading,
  statusMessage,
  overlay,
  className,
}) => {
  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-lg border border-slate-200",
        className ?? "h-[320px]",
      )}
    >
      <MapContainer
        center={center}
        zoom={15}
        zoomControl={false}
        scrollWheelZoom
        className="h-full w-full"
        doubleClickZoom={false}
      >
        <RecenterMap center={center} />
        <TileLayer attribution={MAP_TILE_CONFIG.attributionShort} url={MAP_TILE_CONFIG.url} />
        {polygons.map((polygon, index) => (
          <Polygon
            key={index}
            positions={polygon}
            pathOptions={{
              color: GEOFENCE_COLOR,
              weight: 2,
              fillColor: GEOFENCE_COLOR,
              fillOpacity: 0.3,
            }}
          />
        ))}
        {selectedLocation && (
          <Marker position={[selectedLocation.lat, selectedLocation.lon]} icon={SITE_PIN_ICON}>
            {selectedLocation.name && (
              <Tooltip permanent direction="top" offset={[0, -32]}>
                {selectedLocation.name}
              </Tooltip>
            )}
          </Marker>
        )}
      </MapContainer>
      {overlay && (
        // Leaflet's panes stop at z-index 400 and its controls at 1000, so the
        // overlay has to sit at least that high to receive clicks.
        <div className="absolute left-2 top-2 z-[1000]">{overlay}</div>
      )}
      {(statusMessage || isLoading) && (
        <div className="absolute inset-0 z-[900] flex items-center justify-center bg-white/70 px-6 text-center text-sm text-text-subtle">
          {statusMessage}
        </div>
      )}
    </div>
  );
};
